import { get, sortBy } from "lodash-es";

import { EHotelRateSortType } from "@/share/hotel";

/** 排序默认值：礼遇优先（设计稿「重置」恢复为默认值） */
export const RATE_FILTER_DEFAULT_SORT = [EHotelRateSortType.BY_BENEFIT];

const COMMISSION_AMOUNT_KEY =
  "_biz.bookingPreview.commission.approximate.amount";
const COMMISSION_SHARE_KEY = "_biz.bookingPreview.commission.percent.share";
const COMMISSION_SHARE_ADJUSTMENT_KEY =
  "_biz.bookingPreview.commission.percent.shareAdjustment";

/**
 * rate 的整段行程总价（含税费）：`sumReservation` 优先，其次 `sum`
 * 见 arch-design/CDEH-63678-rate-filter-biz-node.md
 */
export const getRateTotalPrice = rate =>
  Number(
    get(
      rate,
      "priceDetail.aggregated.approximate.sumReservation",
      get(rate, "priceDetail.aggregated.approximate.sum", 0)
    )
  ) || 0;

/** rate 的单晚均价 */
export const getRateAveragePrice = rate =>
  Number(get(rate, "priceDetail.aggregated.approximate.average", 0)) || 0;

/** 预订晚数 */
export const getRateNights = rate =>
  Number(get(rate, "priceDetail.aggregated.quantity", 0)) || 0;

/** rate 与房型排序统一用整段行程总价（同一次行程下与均价排序等价），缺失时退回均价 × 晚数 */
/**
 * 合并 asyncJob 分片返回的 rateFilter
 *
 * `/booking/hotel/rates` 在部分酒店会返回 asyncJobId，后续请求分片返回；
 * 每片的 `_biz.rateFilter` 只用该片的 room 计算，是不完整的。
 * 直接"用新片覆盖旧片"会把中间片当成权威，导致"该选项不存在"→ 已选被裁掉，
 * 因此这里按分组/选项取并集，等最后一片（没有 asyncJobId）再裁剪已选。
 */
export const mergeRateFilterNodes = (prev, next) => {
  if (!prev) return next || null;
  if (!next) return prev;

  const groups = [];
  const groupMap = new Map();

  const ensureGroup = group => {
    if (groupMap.has(group.key)) return groupMap.get(group.key);
    const target = {
      key: group.key,
      labelKey: group.labelKey,
      label: group.label,
      multiple: group.multiple,
      options: []
    };
    groupMap.set(group.key, target);
    groups.push(target);
    return target;
  };

  const pushOption = (target, option) => {
    const exist = target.options.find(item => item.value === option.value);
    if (!exist) {
      target.options.push({ ...option });
      return;
    }
    if (!exist.label && option.label) exist.label = option.label;
    if (typeof option.count === "number") {
      exist.count = Math.max(exist.count || 0, option.count);
    }
  };

  [prev, next].forEach(node => {
    (node.groups || []).forEach(group => {
      const target = ensureGroup(group);
      if (!target.label && group.label) target.label = group.label;
      (group.options || []).forEach(option => pushOption(target, option));
    });
  });

  return {
    version: next.version != null ? next.version : prev.version,
    crossGroupMatch: next.crossGroupMatch || prev.crossGroupMatch,
    groups
  };
};

const getSortPrice = rate =>
  getRateTotalPrice(rate) ||
  getRateAveragePrice(rate) * (getRateNights(rate) || 1);

const getRoomMinPrice = room =>
  (room.rates || []).reduce(
    (min, rate) => Math.min(min, getSortPrice(rate)),
    Number.POSITIVE_INFINITY
  );

const getCommissionAmount = rate =>
  Number(get(rate, COMMISSION_AMOUNT_KEY, 0)) || 0;

const getCommissionTotalShare = rate =>
  (Number(get(rate, COMMISSION_SHARE_KEY, 0)) || 0) +
  (Number(get(rate, COMMISSION_SHARE_ADJUSTMENT_KEY, 0)) || 0);

/** 该 rate 是否命中全部已选筛选项：分组之间取交集（`crossGroupMatch: 'all'`） */
const isRateMatched = (rate, values) => {
  const tags = get(rate, "_biz.rateFilter.tags", []);
  return values.every(value => tags.includes(value));
};

/**
 * 按服务端下发的筛选项 value 过滤房型与 rate
 * @param {Array} rooms 房型列表
 * @param {string[]} values 选中的筛选项 value（`${groupKey}:${optionKey}`）
 */
export const filterRoomsByRateFilterTags = (rooms, values) => {
  const selected = (values || []).filter(Boolean);
  if (!rooms?.length) return [];
  if (!selected.length) return rooms;

  return rooms
    .map(room => ({
      ...room,
      rates: (room.rates || []).filter(rate => isRateMatched(rate, selected))
    }))
    .filter(room => room.rates.length);
};

/** 低价优先 */
const sortRoomsByLowPrice = rooms =>
  sortBy(
    rooms.map(room => ({
      ...room,
      rates: sortBy(room.rates || [], getSortPrice)
    })),
    getRoomMinPrice
  );

/** 礼遇优先：先按低价排，再让有礼遇的 rate 排前面 */
const sortRoomsByBenefit = rooms =>
  sortRoomsByLowPrice(rooms).map(room => ({
    ...room,
    rates: sortBy(room.rates, rate => (rate.benefit ? 0 : 1))
  }));

/** 高佣优先：有佣金的 rate 按总比例、金额倒序排前面 */
const sortRoomsByHighCommission = rooms =>
  sortRoomsByLowPrice(rooms).map(room => {
    const { hasCommission, noCommission } = (room.rates || []).reduce(
      (acc, rate) => {
        getCommissionAmount(rate)
          ? acc.hasCommission.push(rate)
          : acc.noCommission.push(rate);
        return acc;
      },
      { hasCommission: [], noCommission: [] }
    );

    return {
      ...room,
      rates: [
        ...hasCommission.sort((prev, next) => {
          const shareDiff =
            getCommissionTotalShare(next) - getCommissionTotalShare(prev);
          if (shareDiff) return shareDiff;
          return getCommissionAmount(next) - getCommissionAmount(prev);
        }),
        ...noCommission
      ]
    };
  });

const SORT_TYPES_MAP = {
  [EHotelRateSortType.BY_BENEFIT]: sortRoomsByBenefit,
  [EHotelRateSortType.BY_LOW_PRICE]: sortRoomsByLowPrice,
  [EHotelRateSortType.BY_HIGH_COMMISSION]: sortRoomsByHighCommission
};

/**
 * 房型与 rate 排序
 * @param {Array} rooms 房型列表
 * @param {string[]} sortTypes 排序方式（单选，取第一个生效，与移动端一致）
 */
export const sortRoomsByRateSortTypes = (rooms, sortTypes) => {
  if (!rooms?.length || !sortTypes?.length) return rooms || [];
  const sortType = sortTypes[0];
  const sortFn = SORT_TYPES_MAP[sortType];
  return sortFn ? sortFn(rooms) : rooms;
};

/**
 * rate 筛选（Y_RATE_AI_FEATURE_FILTER）统一入口：先按已选筛选项过滤，再排序
 * @param {Array} rooms 房型列表
 * @param {object} selected `{ tags, sortTypes }`
 */
export const applyHotelRateFilter = (rooms, selected = {}) =>
  sortRoomsByRateSortTypes(
    filterRoomsByRateFilterTags(rooms, selected.tags),
    selected.sortTypes?.length ? selected.sortTypes : RATE_FILTER_DEFAULT_SORT
  );
